import React from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';

// layouts
import DashboardLayout from './layouts/dashboard';
import { useAuth } from './context/AuthContext';
import PageLoader from './components/PageLoader';

// LAZY LOADED PAGES
const Login = React.lazy(() => import('./pages/Login'));
const NotFound = React.lazy(() => import('./pages/Page404'));
const Unauthorized = React.lazy(() => import('./pages/Unauthorized'));
const Customer = React.lazy(() => import('./pages/customer/Customer'));
const DashboardApp = React.lazy(() => import('./pages/DashboardApp'));
const AccountDeletionRequest = React.lazy(() => import('./pages/accountDeletionRequest/AccountDeletionRequest'));
const App = React.lazy(() => import('./pages/app/App'));
const DashboardModule = React.lazy(() => import('./pages/dashboardModule/DashboardModule'));
const UserRole = React.lazy(() => import('./pages/userRole/UserRole'));
const DashboardUser = React.lazy(() => import('./pages/dashboardUser/DashboardUser'));
const SubscriptionDeal = React.lazy(() => import('./pages/subscription/SubsciptionDeal/SubscriptionDeal'));
const CustomerSubscription = React.lazy(() => import('./pages/subscription/CustomerSubscription/CustomerSubscription'));
const SubscriptionPayment = React.lazy(() => import('./pages/subscription/SubscriptionPayment/SubscriptionPayment'));
const SubscriptionWebhook = React.lazy(() => import('./pages/subscription/SubscriptionWebhook/SubscriptionWebhook'));
const SubscriptionConfig = React.lazy(() => import('./pages/subscription/SubsciptionConfig/SubscriptionConfig'));
const AiApiKey = React.lazy(() => import('./pages/aiApiKey/AiApiKey'));
const SubscriptionPlan = React.lazy(() => import('./pages/subscription/SubscriptionPlan/SubscriptionPlan'));
const SubscriptionProductPrice = React.lazy(() => import('./pages/subscription/SubscriptionProductPrice/SubscriptionProductPrice'));
const RetentionMessageConfig = React.lazy(() => import('./pages/subscription/RetentionMessageConfig/RetentionMessageConfig'));
const AiMediaTemplate = React.lazy(() => import('./pages/aiMediaTemplate/AiMediaTemplate'));

const AiStoryLanguages = React.lazy(() => import('./pages/storyTune/AiStoryLanguages/AiStoryLanguages'));
const CoverImageCategory = React.lazy(() => import('./pages/storyTune/CoverImageCategorys/CoverImageCategory'));
const CoverImage = React.lazy(() => import('./pages/storyTune/CoverImage/CoverImage'));

const MediaCategory = React.lazy(() => import('./pages/media/MediaCategory/MediaCategory'));
const MediaItem = React.lazy(() => import('./pages/media/MediaItem/MediaItem'));
const InappTransactions = React.lazy(() => import('./pages/inappTransaction/InappTransactions'));
const MediaGenerationJob = React.lazy(() => import('./pages/mediaGenerationJob/MediaGenerationJob'));

const AIRequestWordPricing = React.lazy(() => import('./pages/storyTune/AIRequestWordPricing/AIRequestWordPricing'));
const AiUsageHistory = React.lazy(() => import('./pages/aiUsageHistory/AiUsageHistory'));
const GameModuleIdentifier = React.lazy(() => import('./pages/gameModuleIdentifier/GameModuleIdentifier'));
const CustomerStory = React.lazy(() => import('./pages/storyTune/CustomerStory/CustomerStory'));

const AppConfig = React.lazy(() => import('./pages/appConfig/AppConfig'));
const AdKeyword = React.lazy(() => import('./pages/adKeyword/AdKeyword'));

const AdNetwork = React.lazy(() => import('./pages/adNetwork/AdNetwork'));
const AppVersion = React.lazy(() => import('./pages/appVersion/AppVersion'));
const Printer = React.lazy(() => import('./pages/printer/Printer'));

const VocabularyCategory = React.lazy(() => import('./pages/vocab/VocabularyCategory/VocabularyCategory'));
const Vocabulary = React.lazy(() => import('./pages/vocab/Vocabulary/Vocabulary'));

const AiUsage = React.lazy(() => import('./pages/aiUsage/AiUsage'));

// day planner
const UserProfile = React.lazy(() => import('./pages/dayPlanner/userProfile/UserProfile'));
const DailyReflection = React.lazy(() => import('./pages/dayPlanner/dailyReflection/DailyReflection'));
const DailyStats = React.lazy(() => import('./pages/dayPlanner/dailyStats/DailyStats'));
const FocusSession = React.lazy(() => import('./pages/dayPlanner/focusSession/FocusSession'));
const MoodEntry = React.lazy(() => import('./pages/dayPlanner/moodEntry/MoodEntry'));
const RoutinePreset = React.lazy(() => import('./pages/dayPlanner/routinePreset/RoutinePreset'));
const ScheduleBlock = React.lazy(() => import('./pages/dayPlanner/scheduleBlock/ScheduleBlock'));
const TaskList = React.lazy(() => import('./pages/dayPlanner/taskList/TaskList'));
const Task = React.lazy(() => import('./pages/dayPlanner/task/Task'));

// selfcare pet
const SPShopItem = React.lazy(() => import('./pages/selfcarePet/shopItem/ShopItem'));
const SPExercise = React.lazy(() => import('./pages/selfcarePet/exercise/Exercise'));
const SPAdventureLocation = React.lazy(() => import('./pages/selfcarePet/adventureLocation/AdventureLocation'));
const SPPetOwner = React.lazy(() => import('./pages/selfcarePet/petOwner/PetOwner'));
const SPPet = React.lazy(() => import('./pages/selfcarePet/pet/Pet'));
const SPGoal = React.lazy(() => import('./pages/selfcarePet/goal/Goal'));
const SPCheckIn = React.lazy(() => import('./pages/selfcarePet/checkIn/CheckIn'));
const SPCurrencyTransaction = React.lazy(() => import('./pages/selfcarePet/currencyTransaction/CurrencyTransaction'));
const SPAdventure = React.lazy(() => import('./pages/selfcarePet/adventure/Adventure'));
const SPFriendship = React.lazy(() => import('./pages/selfcarePet/friendship/Friendship'));
const SPVibe = React.lazy(() => import('./pages/selfcarePet/vibe/Vibe'));

// recimeal
const RMRecipeTag = React.lazy(() => import('./pages/recimeal/recipeTag/RecipeTag'));
const RMGrocery = React.lazy(() => import('./pages/recimeal/grocery/Grocery'));
const RMCookbook = React.lazy(() => import('./pages/recimeal/cookbook/Cookbook'));
const RMRecipe = React.lazy(() => import('./pages/recimeal/recipe/Recipe'));
const RMMealPlan = React.lazy(() => import('./pages/recimeal/mealPlan/MealPlan'));

// key connect
const CommandBudget = React.lazy(() => import('./pages/key-connect/CommandBudget/CommandBudget'));
const CommandLog = React.lazy(() => import('./pages/key-connect/CommandLog/CommandLog'));
const SmartCarLink = React.lazy(() => import('./pages/key-connect/SmartCarLink/SmartCarLink'));
const StateCache = React.lazy(() => import('./pages/key-connect/StateCache/StateCache'));
const WebhookEvent = React.lazy(() => import('./pages/key-connect/WebhookEvent/WebhookEvent'));
const SyncFile = React.lazy(() => import('./pages/key-connect/SyncFile/SyncFile'));
const SyncRecord = React.lazy(() => import('./pages/key-connect/SyncRecord/SyncRecord'));


// ----------------------------------------------------------------------
const ProtectedRoute = ({ children }) => {
  const { authState } = useAuth();
  const location = useLocation();
  if (!authState.token) return <Navigate to="/login" state={{ path: location.pathname }} />;
  return children;
};
const LazyComponent = ({ children }) => {
  return <React.Suspense fallback={<PageLoader />}>{children}</React.Suspense>;
};

export default function Router() {
  const { authState } = useAuth();
  return useRoutes([
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        //  { path: '/', element: <Navigate to={authState?.user?.menu_items[0]?.path} /> },
        {
          path: '/dashboard',
          element: (
            <LazyComponent>
              <DashboardApp />
            </LazyComponent>
          ),
        },
        {
          path: 'customers',
          element: (
            <LazyComponent>
              <Customer />
            </LazyComponent>
          ),
        },
        {
          path: 'account-deletion-request',
          element: (
            <LazyComponent>
              <AccountDeletionRequest />
            </LazyComponent>
          ),
        },
        {
          path: 'apps',
          element: (
            <LazyComponent>
              <App />
            </LazyComponent>
          ),
        },
        {
          path: 'dashboard-modules',
          element: (
            <LazyComponent>
              <DashboardModule />
            </LazyComponent>
          ),
        },
        {
          path: 'roles',
          element: (
            <LazyComponent>
              <UserRole />
            </LazyComponent>
          ),
        },
        {
          path: 'dashboard-user',
          element: (
            <LazyComponent>
              <DashboardUser />
            </LazyComponent>
          ),
        },
        {
          path: 'subscription-deals',
          element: (
            <LazyComponent>
              <SubscriptionDeal />
            </LazyComponent>
          ),
        },
        {
          path: 'customer-subscriptions',
          element: (
            <LazyComponent>
              <CustomerSubscription />
            </LazyComponent>
          ),
        },
        {
          path: 'subscription-payments',
          element: (
            <LazyComponent>
              <SubscriptionPayment />
            </LazyComponent>
          ),
        },
        {
          path: 'subscription-webhooks',
          element: (
            <LazyComponent>
              <SubscriptionWebhook />
            </LazyComponent>
          ),
        },
        {
          path: 'subscription-configs',
          element: (
            <LazyComponent>
              <SubscriptionConfig />
            </LazyComponent>
          ),
        },
        {
          path: 'subscription-plans',
          element: (
            <LazyComponent>
              <SubscriptionPlan />
            </LazyComponent>
          ),
        },
        {
          path: 'subscription-product-prices',
          element: (
            <LazyComponent>
              <SubscriptionProductPrice />
            </LazyComponent>
          ),
        },
        {
          path: 'retention-message-configs',
          element: (
            <LazyComponent>
              <RetentionMessageConfig />
            </LazyComponent>
          ),
        },
        {
          path: 'ai-api-keys',
          element: (
            <LazyComponent>
              <AiApiKey />
            </LazyComponent>
          ),
        },
        {
          path: 'ai-media-templates',
          element: (
            <LazyComponent>
              <AiMediaTemplate />
            </LazyComponent>
          ),
        },
        {
          path: 'ai-story-languages',
          element: (
            <LazyComponent>
              <AiStoryLanguages />
            </LazyComponent>
          ),
        },
        {
          path: 'cover-image-categorys',
          element: (
            <LazyComponent>
              <CoverImageCategory />
            </LazyComponent>
          ),
        },
        {
          path: 'cover-images',
          element: (
            <LazyComponent>
              <CoverImage />
            </LazyComponent>
          ),
        },
        {
          path: 'media-categorys',
          element: (
            <LazyComponent>
              <MediaCategory />
            </LazyComponent>
          ),
        },
        {
          path: 'media-items',
          element: (
            <LazyComponent>
              <MediaItem />
            </LazyComponent>
          ),
        },
        {
          path: 'media-categories',
          element: (
            <LazyComponent>
              <CoverImage />
            </LazyComponent>
          ),
        },
        {
          path: 'inapp-transactions',
          element: (
            <LazyComponent>
              <InappTransactions />
            </LazyComponent>
          ),
        },
        {
          path: 'ai-request-word-pricing',
          element: (
            <LazyComponent>
              <AIRequestWordPricing />
            </LazyComponent>
          ),
        },
        {
          path: 'media-generation-jobs',
          element: (
            <LazyComponent>
              <MediaGenerationJob />
            </LazyComponent>
          ),
        },
        {
          path: 'ai-usage-history',
          element: (
            <LazyComponent>
              <AiUsageHistory />
            </LazyComponent>
          ),
        },
        {
          path: 'ai-usages',
          element: (
            <LazyComponent>
              <AiUsage />
            </LazyComponent>
          ),
        },
        {
          path: 'game-module-identifier',
          element: (
            <LazyComponent>
              <GameModuleIdentifier />
            </LazyComponent>
          ),
        },
        {
          path: 'app-config',
          element: (
            <LazyComponent>
              <AppConfig />
            </LazyComponent>
          ),
        },
        {
          path: 'ad-keywords',
          element: (
            <LazyComponent>
              <AdKeyword />
            </LazyComponent>
          ),
        },
        {
          path: 'customer-story',
          element: (
            <LazyComponent>
              <CustomerStory />
            </LazyComponent>
          ),
        },

        {
          path: 'ad-network',
          element: (
            <LazyComponent>
              <AdNetwork />
            </LazyComponent>
          ),
        },
        {
          path: 'app-version',
          element: (
            <LazyComponent>
              <AppVersion />
            </LazyComponent>
          ),
        },
        {
          path: 'printers',
          element: (
            <LazyComponent>
              <Printer />
            </LazyComponent>
          ),
        },

        {
          path: 'vocabulary-categories',
          element: (
            <LazyComponent>
              <VocabularyCategory />
            </LazyComponent>
          ),
        },
        {
          path: 'vocabularies',
          element: (
            <LazyComponent>
              <Vocabulary />
            </LazyComponent>
          ),
        },

        // day planner
        {
          path: 'user-profile',
          element: (
            <LazyComponent>
              <UserProfile />
            </LazyComponent>
          ),
        },
        {
          path: 'daily-reflection',
          element: (
            <LazyComponent>
              <DailyReflection />
            </LazyComponent>
          ),
        },
        {
          path: 'daily-stats',
          element: (
            <LazyComponent>
              <DailyStats />
            </LazyComponent>
          ),
        },
        {
          path: 'focus-session',
          element: (
            <LazyComponent>
              <FocusSession />
            </LazyComponent>
          ),
        },
        {
          path: 'mood-entry',
          element: (
            <LazyComponent>
              <MoodEntry />
            </LazyComponent>
          ),
        },
        {
          path: 'routine-preset',
          element: (
            <LazyComponent>
              <RoutinePreset />
            </LazyComponent>
          ),
        },
        {
          path: 'schedule-block',
          element: (
            <LazyComponent>
              <ScheduleBlock />
            </LazyComponent>
          ),
        },
        {
          path: 'task-list',
          element: (
            <LazyComponent>
              <TaskList />
            </LazyComponent>
          ),
        },
        {
          path: 'task',
          element: (
            <LazyComponent>
              <Task />
            </LazyComponent>
          ),
        },
        {
          path: 'shop-items',
          element: (
            <LazyComponent>
              <SPShopItem />
            </LazyComponent>
          ),
        },
        {
          path: 'exercises',
          element: (
            <LazyComponent>
              <SPExercise />
            </LazyComponent>
          ),
        },
        {
          path: 'adventure-locations',
          element: (
            <LazyComponent>
              <SPAdventureLocation />
            </LazyComponent>
          ),
        },
        {
          path: 'pet-owners',
          element: (
            <LazyComponent>
              <SPPetOwner />
            </LazyComponent>
          ),
        },
        {
          path: 'pets',
          element: (
            <LazyComponent>
              <SPPet />
            </LazyComponent>
          ),
        },
        {
          path: 'goals',
          element: (
            <LazyComponent>
              <SPGoal />
            </LazyComponent>
          ),
        },
        {
          path: 'check-ins',
          element: (
            <LazyComponent>
              <SPCheckIn />
            </LazyComponent>
          ),
        },
        {
          path: 'currency-transactions',
          element: (
            <LazyComponent>
              <SPCurrencyTransaction />
            </LazyComponent>
          ),
        },
        {
          path: 'adventures',
          element: (
            <LazyComponent>
              <SPAdventure />
            </LazyComponent>
          ),
        },
        {
          path: 'friendships',
          element: (
            <LazyComponent>
              <SPFriendship />
            </LazyComponent>
          ),
        },
        {
          path: 'vibes',
          element: (
            <LazyComponent>
              <SPVibe />
            </LazyComponent>
          ),
        },

        // recimeal
        {
          path: 'recipe-tags',
          element: (
            <LazyComponent>
              <RMRecipeTag />
            </LazyComponent>
          ),
        },
        {
          path: 'groceries',
          element: (
            <LazyComponent>
              <RMGrocery />
            </LazyComponent>
          ),
        },
        {
          path: 'cookbooks',
          element: (
            <LazyComponent>
              <RMCookbook />
            </LazyComponent>
          ),
        },
        {
          path: 'recipes',
          element: (
            <LazyComponent>
              <RMRecipe />
            </LazyComponent>
          ),
        },
        {
          path: 'meal-plans',
          element: (
            <LazyComponent>
              <RMMealPlan />
            </LazyComponent>
          ),
        },

        // key-connect
        {
          path: 'key-connect/command-budgets',
          element: (
            <LazyComponent>
              <CommandBudget />
            </LazyComponent>
          ),
        },
        {
          path: 'key-connect/command-logs',
          element: (
            <LazyComponent>
              <CommandLog />
            </LazyComponent>
          ),
        },
        {
          path: 'key-connect/smart-car-link',
          element: (
            <LazyComponent>
              <SmartCarLink />
            </LazyComponent>
          ),
        },
        {
          path: 'key-connect/state-cache',
          element: (
            <LazyComponent>
              <StateCache />
            </LazyComponent>
          ),
        },
        {
          path: 'key-connect/webhook-events',
          element: (
            <LazyComponent>
              <WebhookEvent />
            </LazyComponent>
          ),
        },
        {
          path: 'key-connect/sync-files',
          element: (
            <LazyComponent>
              <SyncFile />
            </LazyComponent>
          ),
        },
        {
          path: 'key-connect/sync-records',
          element: (
            <LazyComponent>
              <SyncRecord />
            </LazyComponent>
          ),
        },
      ],
    },

    {
      path: '/login',
      element: (
        <LazyComponent>
          <Login />
        </LazyComponent>
      ),
    },
    {
      path: '/unauthorized',
      element: (
        <LazyComponent>
          <Unauthorized />
        </LazyComponent>
      ),
    },
    {
      path: '/404',
      element: (
        <LazyComponent>
          <NotFound />
        </LazyComponent>
      ),
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
